import { call, put } from 'redux-saga/effects';

import { upsertWorkflowCampaign } from 'api';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { RestCampaign, Workflow } from 'modules/workflows/types';
import noop from 'utils/noop';
import { UpsertWorkflowCampaignAction, actions } from '../actions';
import { getConversionNamesString } from './sagaUtils';

export default function* upsertWorkflowCampaignSaga({ payload }: UpsertWorkflowCampaignAction) {
  const { workflow, node, campaign, messageMedium, onFinish = noop } = payload;
  const { id: workflowNodeId, nodeData } = node;
  const isNewNode = Number(workflowNodeId) < 0;

  const DEFAULT_ERROR_MESSAGE = `Something went wrong. Click on ${
    isNewNode ? 'Create' : 'Update'
  } campaign to try again.`;

  const SUCCESS_MESSAGE = isNewNode
    ? 'Success! Created new campaign.'
    : 'Success! Saved changes to campaign.';

  // TODO: something weird is going on with the conversions type.  Has it changed?  Review.
  // @ts-ignore
  const conversions = campaign?.conversions;
  const CONVERSIONS_NAMES_MESSAGE = getConversionNamesString(conversions);
  const CONVERSIONS_MESSAGE =
    conversions?.length > 1
      ? `Conversion events ${CONVERSIONS_NAMES_MESSAGE} now available as Goals`
      : `Conversion event ${CONVERSIONS_NAMES_MESSAGE} now available as a Goal`;

  try {
    yield put(actions.isUpdatingCampaignDetails(true));

    const data: { nodeId: number; campaign: RestCampaign } = yield call(upsertWorkflowCampaign, {
      workflowId: workflow.id as number,
      workflowType: workflow.workflowType,
      nodeId: node.id,
      x: node.x,
      y: node.y,
      campaignId: nodeData.campaignId,
      templateId: nodeData.templateId,
      messageMedium,
      campaignMetadata: campaign,
      sendNodeClass: payload.nodeClass || nodeData.nodeClass,
    });
    const { nodeId: updatedNodeId, campaign: updatedCampaign } = data;

    /**
     * This was a pre-drafts way to update campaigns.  It updated campaigns
     * listed in `campaignMetadata` in the workflow.
     *
     * I do not believe this has been tested with CHO, so if we are to use it
     * again then CHO will need to be tested.
     *
     * REVIEW: do we still need to keep this now that drafts is GA'd?
     */
    const newCampaignMetaData: Workflow['campaignMetadata'] = {
      ...workflow?.campaignMetadata,
      [updatedNodeId]: {
        ...(workflow?.campaignMetadata?.[updatedNodeId] || {}),
        ...campaign,
        // fyi, we're mixing campaign data into campaignMetadata here...
        // and they are not exactly equivalent
        // and may or may not have existing unintended references
        ...updatedCampaign,

        // the field(s) below are not returned as part of the campaign
        // object by /upsertWorkflowCampaign
        // so we are inserting the original values from the request
        // this is not ideal...
        // but probably the least risky/invasive workaround given
        // the current state of the endpoint
        labels: campaign?.labels,
        nodeId: `${updatedNodeId}`,
      },
    };

    // only do this if node is new
    if (isNewNode) {
      delete newCampaignMetaData[workflowNodeId];
    }

    const updatedWorkflowData = { campaignMetadata: newCampaignMetaData };

    yield put(actions.updateWorkflow(updatedWorkflowData));
    yield put(addPositiveToast(SUCCESS_MESSAGE));

    if (CONVERSIONS_NAMES_MESSAGE) {
      yield put(addPositiveToast(CONVERSIONS_MESSAGE));
    }

    const nextNodeData = {
      ...nodeData,
      campaignId: updatedCampaign.id,
      templateId: updatedCampaign.templateId,
      name: updatedCampaign.name,
    };

    yield call(onFinish, nextNodeData, `${updatedNodeId}`, updatedCampaign);
  } catch (error) {
    const { onFailure = noop } = payload;
    yield call(onFailure);
    yield put(addNegativeToastWithErrorId(DEFAULT_ERROR_MESSAGE, 'upsertWorkflowCampaignSaga'));
  } finally {
    yield put(actions.isUpdatingCampaignDetails(false));
  }
}
